import type { TemplateRegistration, TemplateData } from './types';
import { sanitizeText, sanitizeSeoText, escapeHtml, escapeAttr } from './sanitize';
import { getIconSvg, renderStars, buildFontsUrl, buildNavLinks, NavDataFlags } from './helpers';

const ALL_FEATURES = [
  'about-section', 'services-list', 'testimonials', 'contact-form',
  'phone-display', 'address-map', 'business-hours', 'social-links',
  'faq', 'price-list', 'promotions', 'photo-gallery', 'google-reviews',
];

export const classicEditorial: TemplateRegistration = {
  meta: {
    id: 'classic-editorial',
    name: 'Classic Editorial',
    category: 'Classic',
    description: 'Newspaper-inspired editorial design with serif typography, thin rules, and magazine-style layout',
    previewImage: '/previews/classic-editorial.png',
    supportedFeatures: ALL_FEATURES,
    defaultEnabledFeatures: [
      'about-section', 'services-list', 'testimonials', 'contact-form',
      'phone-display', 'address-map', 'business-hours', 'social-links',
    ],
  },

  render(data: TemplateData, enabledFeatures: Set<string>): string {
    const b = data.businessName;
    const bSafe = sanitizeText(b);
    const headline = sanitizeText(data.headline);
    const aboutText = sanitizeText(data.aboutText);
    const seoTitle = sanitizeSeoText(data.seo?.title || b, 70);
    const seoDesc = sanitizeSeoText(data.seo?.description || data.headline, 160);
    const fontsUrl = buildFontsUrl(['Playfair Display', 'Source Serif Pro']);

    const phone = data.businessData?.phone || '';
    const contactEmail = data.businessData?.contactEmail || null;
    const address = data.businessData?.address || '';
    const rating = data.businessData?.rating || 0;
    const reviewCount = data.businessData?.reviewCount || 0;
    const hours = data.businessData?.businessHours || [];
    const photos = data.businessData?.photos || [];
    const mapsUrl = data.businessData?.googleMapsUrl || '';

    const heroPhoto = photos.length > 0 ? photos[0] : '';
    const galleryPhotos = photos.slice(1, 8);
    const navDataFlags: NavDataFlags = {
      hasServices: data.services.length > 0,
      hasTestimonials: data.testimonials.length > 0,
      hasGalleryPhotos: galleryPhotos.length > 0,
      hasFaq: !!(data.faq && data.faq.length > 0),
      hasPriceList: !!(data.priceList && data.priceList.length > 0),
    };
    const navLinks = buildNavLinks(enabledFeatures, navDataFlags);

    // --- Sections ---

    const navHtml = navLinks.map(l =>
      `<a href="${escapeAttr(l.href)}" class="nav-link">${escapeHtml(l.label)}</a>`
    ).join('');

    const promotionHtml = enabledFeatures.has('promotions') && data.promotion ? `
      <div class="promo-banner">
        <div class="promo-inner">
          <span class="promo-discount">${sanitizeText(data.promotion.discount)}</span>
          <span class="promo-title">${sanitizeText(data.promotion.title)}</span>
          <span class="promo-desc">${sanitizeText(data.promotion.description)}</span>
        </div>
      </div>` : '';

    const heroHtml = `
      <section class="hero">
        ${heroPhoto ? `<div class="hero-cover-img"><img src="${escapeAttr(heroPhoto)}" alt="${escapeAttr(b)}" /></div>` : ''}
        <div class="hero-cover-overlay"></div>
        <div class="hero-text">
          <h1 class="hero-headline">${headline}</h1>
          <hr class="thin-rule" style="border-color:rgba(255,255,255,.3);" />
          <p class="hero-sub">${bSafe}</p>
          ${enabledFeatures.has('phone-display') && phone ? `<p class="hero-phone">${getIconSvg('phone')} ${sanitizeText(phone)}</p>` : ''}
          ${enabledFeatures.has('google-reviews') && rating > 0 ? `<div class="hero-rating">${renderStars(rating)} <span class="rating-text">${rating.toFixed(1)} (${reviewCount} reviews)</span></div>` : ''}
        </div>
      </section>`;

    const aboutHtml = enabledFeatures.has('about-section') ? `
      <section id="about" class="section about-section">
        <h2 class="section-title">About</h2>
        <hr class="thin-rule" />
        <div class="about-body">
          <p class="drop-cap">${aboutText}</p>
        </div>
      </section>` : '';

    const servicesHtml = enabledFeatures.has('services-list') && data.services.length > 0 ? `
      <section id="services" class="section services-section">
        <h2 class="section-title">Our Services</h2>
        <hr class="thin-rule" />
        <div class="services-list">
          ${data.services.map((s, i) => `
            <div class="service-row">
              <span class="service-num">${String(i + 1).padStart(2, '0')}</span>
              <div class="service-content">
                <h3 class="service-name">${sanitizeText(s.name)}</h3>
                <p class="service-desc">${sanitizeText(s.description)}</p>
                ${s.price ? `<span class="service-price">${sanitizeText(s.price)}</span>` : ''}
              </div>
            </div>
            ${i < data.services.length - 1 ? '<hr class="thin-rule section-divider" />' : ''}
          `).join('')}
        </div>
      </section>` : '';

    const testimonialsHtml = enabledFeatures.has('testimonials') && data.testimonials.length > 0 ? `
      <section id="testimonials" class="section testimonials-section">
        <h2 class="section-title">What People Say</h2>
        <hr class="thin-rule" />
        <div class="testimonials-list">
          ${data.testimonials.map(t => `
            <blockquote class="pull-quote">
              <span class="quote-mark">&ldquo;</span>
              <p class="quote-text">${sanitizeText(t.content)}</p>
              <footer class="quote-footer">
                <cite class="quote-author">${sanitizeText(t.name)}</cite>
                ${t.role ? `<span class="quote-role">${sanitizeText(t.role)}</span>` : ''}
                ${t.rating ? `<div class="quote-stars">${renderStars(t.rating)}</div>` : ''}
              </footer>
            </blockquote>
          `).join('')}
        </div>
      </section>` : '';

    const galleryHtml = enabledFeatures.has('photo-gallery') && photos.length > 1 ? `
      <section id="gallery" class="section gallery-section">
        <h2 class="section-title">Gallery</h2>
        <hr class="thin-rule" />
        <div class="gallery-grid">
          ${photos.slice(0, 8).map(p => `<div class="gallery-item"><img src="${escapeAttr(p)}" alt="${escapeAttr(b)} photo" loading="lazy" /></div>`).join('')}
        </div>
      </section>` : '';

    const faqHtml = enabledFeatures.has('faq') && data.faq && data.faq.length > 0 ? `
      <section id="faq" class="section faq-section">
        <h2 class="section-title">Frequently Asked Questions</h2>
        <hr class="thin-rule" />
        <div class="faq-list">
          ${data.faq.map(f => `
            <details class="faq-item">
              <summary class="faq-question">${sanitizeText(f.question)}</summary>
              <p class="faq-answer">${sanitizeText(f.answer)}</p>
            </details>
          `).join('')}
        </div>
      </section>` : '';

    const priceHtml = enabledFeatures.has('price-list') && data.priceList && data.priceList.length > 0 ? `
      <section id="pricing" class="section pricing-section">
        <h2 class="section-title">Pricing</h2>
        <hr class="thin-rule" />
        <div class="price-list">
          ${data.priceList.map(p => `
            <div class="price-row">
              <div class="price-info">
                <span class="price-name">${sanitizeText(p.name)}</span>
                <span class="price-dots"></span>
                <span class="price-amount">${sanitizeText(p.price)}</span>
              </div>
              <p class="price-desc">${sanitizeText(p.description)}</p>
            </div>
          `).join('')}
        </div>
      </section>` : '';

    const hoursHtml = enabledFeatures.has('business-hours') && hours.length > 0 ? `
      <section class="section hours-section">
        <h2 class="section-title">Business Hours</h2>
        <hr class="thin-rule" />
        <ul class="hours-list">
          ${hours.map(h => `<li>${sanitizeText(h)}</li>`).join('')}
        </ul>
      </section>` : '';

    const contactHtml = enabledFeatures.has('contact-form') ? `
      <section id="contact" class="section contact-section">
        <h2 class="section-title">Get in Touch</h2>
        <hr class="thin-rule" />
        <div class="contact-inner">
          <div class="contact-details-row">
            ${enabledFeatures.has('phone-display') && phone ? `<p class="contact-detail">${getIconSvg('phone')} ${sanitizeText(phone)}</p>` : ''}
            ${enabledFeatures.has('address-map') && address ? `<p class="contact-detail">${getIconSvg('map-pin')} ${sanitizeText(address)}</p>` : ''}
            ${enabledFeatures.has('address-map') && mapsUrl ? `<a href="${escapeAttr(mapsUrl)}" target="_blank" rel="noopener" class="map-link">View on Google Maps</a>` : ''}
          </div>
          <form class="contact-form" ${contactEmail ? `action="https://formsubmit.co/${escapeAttr(contactEmail)}" method="POST"` : 'onsubmit="return false;"'}>
            <div class="form-row">
              <input type="text" placeholder="Your Name" class="form-input" required />
              <input type="email" placeholder="Email Address" class="form-input" required />
              <textarea placeholder="Your Message" class="form-textarea" rows="2" required></textarea>
            </div>
            <button type="submit" class="form-btn">Send Message</button>
          </form>
        </div>
      </section>` : '';

    const footerHtml = `
      <footer class="site-footer">
        <hr class="thin-rule" />
        <div class="footer-inner">
          <p class="footer-brand">${bSafe}</p>
          ${enabledFeatures.has('social-links') ? `
          <div class="social-links">
            <a href="#" aria-label="Facebook" class="social-link">Facebook</a>
            <a href="#" aria-label="Twitter" class="social-link">Twitter</a>
            <a href="#" aria-label="Instagram" class="social-link">Instagram</a>
          </div>` : ''}
          <p class="footer-copy">${bSafe}</p>
        </div>
      </footer>`;

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${seoTitle}</title>
  <meta name="description" content="${seoDesc}" />
  <link rel="preconnect" href="https://fonts.googleapis.com" />
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
  <link href="${fontsUrl}" rel="stylesheet" />
  <style>
    *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }

    :root {
      --bg: #faf9f7;
      --text: #1e293b;
      --accent: #2563eb;
      --muted: #64748b;
      --border: #d4d0c8;
      --heading-font: 'Playfair Display', Georgia, serif;
      --body-font: 'Source Serif Pro', 'Georgia', serif;
    }

    html { scroll-behavior: smooth; }

    body {
      font-family: var(--body-font);
      color: var(--text);
      background: var(--bg);
      line-height: 1.75;
      font-size: 17px;
      -webkit-font-smoothing: antialiased;
    }

    /* NAV */
    .site-nav {
      position: sticky; top: 0; z-index: 100;
      background: var(--bg);
      border-bottom: 1px solid var(--border);
      padding: 0 24px;
    }
    .nav-inner {
      max-width: 960px; margin: 0 auto;
      display: flex; align-items: center; justify-content: space-between;
      height: 56px;
    }
    .nav-brand {
      font-family: var(--heading-font);
      font-size: 1.25rem; font-weight: 700;
      color: var(--text); text-decoration: none;
      letter-spacing: -0.02em;
    }
    .nav-links { display: flex; gap: 24px; }
    .nav-toggle { display: none; background: none; border: none; cursor: pointer; }
    .nav-toggle span { display: block; width: 22px; height: 2px; background: var(--text); margin: 5px 0; }
    .nav-link {
      font-family: var(--body-font);
      font-size: 0.85rem; color: var(--muted);
      text-decoration: none; text-transform: uppercase;
      letter-spacing: 0.08em; font-weight: 400;
      transition: color 0.2s;
    }
    .nav-link:hover { color: var(--text); }

    /* THIN RULE */
    .thin-rule {
      border: none;
      border-top: 1px solid var(--border);
      margin: 16px 0;
    }

    /* PROMO */
    .promo-banner {
      background: var(--accent); color: #fff; text-align: center; padding: 10px 24px;
      font-size: 0.9rem; font-family: var(--body-font);
    }
    .promo-inner { max-width: 960px; margin: 0 auto; }
    .promo-discount { font-weight: 700; margin-right: 8px; }
    .promo-title { margin-right: 8px; }
    .promo-desc { opacity: 0.9; }

    /* HERO - magazine cover: full-bleed image, large editorial text bottom-left */
    .hero { position: relative; min-height: 100vh; display: flex; align-items: flex-end; overflow: hidden; }
    .hero-cover-img { position: absolute; inset: 0; }
    .hero-cover-img img { width: 100%; height: 100%; object-fit: cover; display: block; }
    .hero-cover-overlay { position: absolute; inset: 0; background: linear-gradient(to top, rgba(0,0,0,.7) 0%, rgba(0,0,0,.2) 40%, transparent 70%); }
    .hero-text { position: relative; z-index: 2; max-width: 960px; margin: 0 auto; width: 100%; padding: 0 24px 64px; }
    .hero-headline {
      font-family: var(--heading-font);
      font-size: clamp(3rem, 8vw, 5.5rem); line-height: 1.05;
      font-weight: 700; letter-spacing: -0.02em;
      color: #fff;
    }
    .hero-sub {
      font-size: 1.15rem; color: rgba(255,255,255,.8);
      margin-top: 12px; font-style: italic; max-width: 500px;
    }
    .hero-phone {
      margin-top: 16px; font-size: 0.95rem; color: rgba(255,255,255,.7);
      display: flex; align-items: center; gap: 8px;
    }
    .hero-phone .icon-char { font-size: 18px; line-height: 1; }
    .hero-rating {
      margin-top: 12px; display: flex; align-items: center; gap: 8px;
    }
    .star.filled { color: #d97706; }
    .star { color: #d4d0c8; }
    .rating-text { font-size: 0.85rem; color: rgba(255,255,255,.7); }

    /* SECTIONS */
    .section {
      max-width: 800px; margin: 0 auto; padding: 48px 24px;
    }
    .section-title {
      font-family: var(--heading-font);
      font-size: 2rem; font-weight: 700;
      letter-spacing: -0.02em; margin-bottom: 4px;
    }

    /* ABOUT - DROP CAP */
    .drop-cap::first-letter {
      font-family: var(--heading-font);
      float: left; font-size: 4.5rem; line-height: 0.8;
      padding-right: 12px; padding-top: 6px;
      color: var(--accent); font-weight: 700;
    }
    .about-body p { font-size: 1.05rem; line-height: 1.85; }

    /* SERVICES - numbered list */
    .service-row {
      display: flex; gap: 24px; align-items: baseline;
      padding: 24px 0;
    }
    .service-num {
      font-family: var(--heading-font); font-size: 2.5rem; font-weight: 700;
      color: var(--accent); line-height: 1; flex-shrink: 0; min-width: 48px;
    }
    .service-content { flex: 1; }
    .service-name {
      font-family: var(--heading-font);
      font-size: 1.3rem; font-weight: 600; margin-bottom: 4px;
    }
    .service-desc { color: var(--muted); font-size: 0.95rem; }
    .service-price {
      display: inline-block; margin-top: 8px;
      font-weight: 600; color: var(--accent); font-size: 0.95rem;
    }
    .service-icon { display: none; }
    .section-divider { margin: 0; }

    /* TESTIMONIALS - PULL QUOTES */
    .testimonials-list { display: flex; flex-direction: column; gap: 32px; margin-top: 24px; }
    .pull-quote {
      position: relative; padding: 24px 32px 24px 56px;
      border-left: 3px solid var(--accent);
    }
    .quote-mark {
      position: absolute; left: 12px; top: 8px;
      font-family: var(--heading-font);
      font-size: 4rem; line-height: 1; color: var(--accent); opacity: 0.3;
    }
    .quote-text {
      font-size: 1.1rem; font-style: italic; line-height: 1.7;
      color: var(--text);
    }
    .quote-footer { margin-top: 12px; }
    .quote-author {
      font-family: var(--heading-font);
      font-style: normal; font-weight: 600; font-size: 1rem;
    }
    .quote-role {
      display: block; font-size: 0.85rem; color: var(--muted); font-style: normal;
    }
    .quote-stars { margin-top: 4px; }

    /* GALLERY */
    .gallery-grid {
      display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
      gap: 12px; margin-top: 24px;
    }
    .gallery-item img {
      width: 100%; aspect-ratio: 1; object-fit: cover;
      border: 1px solid var(--border);
    }

    /* FAQ */
    .faq-list { margin-top: 24px; }
    .faq-item {
      border-bottom: 1px solid var(--border); padding: 16px 0;
    }
    .faq-question {
      font-family: var(--heading-font);
      font-size: 1.1rem; font-weight: 600; cursor: pointer;
      list-style: none;
    }
    .faq-question::-webkit-details-marker { display: none; }
    .faq-question::before {
      content: '+'; display: inline-block; width: 24px;
      font-family: var(--body-font); color: var(--accent);
      font-weight: 400; font-size: 1.2rem;
    }
    details[open] .faq-question::before { content: '\\2212'; }
    .faq-answer {
      margin-top: 8px; padding-left: 24px;
      color: var(--muted); font-size: 0.95rem; line-height: 1.7;
    }

    /* PRICING */
    .price-list { margin-top: 24px; }
    .price-row { padding: 16px 0; border-bottom: 1px solid var(--border); }
    .price-info {
      display: flex; align-items: baseline; gap: 8px;
    }
    .price-name {
      font-family: var(--heading-font);
      font-weight: 600; font-size: 1.1rem; white-space: nowrap;
    }
    .price-dots {
      flex: 1; border-bottom: 1px dotted var(--border);
      margin-bottom: 4px;
    }
    .price-amount { font-weight: 700; color: var(--accent); white-space: nowrap; }
    .price-desc { color: var(--muted); font-size: 0.85rem; margin-top: 4px; }

    /* HOURS */
    .hours-list {
      list-style: none; margin-top: 24px;
      columns: 2; column-gap: 32px;
    }
    .hours-list li {
      padding: 6px 0; border-bottom: 1px solid var(--border);
      font-size: 0.95rem; color: var(--muted);
      break-inside: avoid;
    }

    /* CONTACT - full-width compact form */
    .contact-inner {
      margin-top: 24px;
    }
    .contact-details-row {
      display: flex; gap: 24px; margin-bottom: 24px; flex-wrap: wrap;
    }
    .contact-detail {
      display: flex; align-items: center; gap: 8px;
      font-size: 0.95rem; color: var(--muted);
    }
    .contact-detail .icon-char { font-size: 18px; line-height: 1; color: var(--accent); }
    .map-link {
      color: var(--accent); font-size: 0.9rem; text-decoration: underline;
    }
    .form-row {
      display: flex; gap: 12px; flex-wrap: wrap;
    }
    .form-row .form-input { flex: 1; min-width: 180px; }
    .form-row .form-textarea { flex: 2; min-width: 240px; }
    .form-input, .form-textarea {
      padding: 10px 14px; border: 1px solid var(--border);
      font-family: var(--body-font); font-size: 0.95rem;
      background: #fff; color: var(--text);
    }
    .form-input:focus, .form-textarea:focus {
      outline: none; border-color: var(--accent);
    }
    .form-textarea { resize: vertical; }
    .form-btn {
      padding: 12px 32px; background: var(--text); color: #fff;
      border: none; font-family: var(--heading-font); font-size: 1rem;
      font-weight: 600; cursor: pointer; letter-spacing: 0.02em;
      transition: background 0.2s; margin-top: 12px;
    }
    .form-btn:hover { background: var(--accent); }

    /* FOOTER */
    .site-footer {
      max-width: 800px; margin: 0 auto; padding: 32px 24px 48px;
      text-align: center;
    }
    .footer-brand {
      font-family: var(--heading-font);
      font-size: 1.1rem; font-weight: 700; margin-bottom: 12px;
    }
    .social-links { display: flex; justify-content: center; gap: 24px; margin-bottom: 12px; }
    .social-link {
      font-size: 0.85rem; color: var(--muted); text-decoration: none;
      text-transform: uppercase; letter-spacing: 0.06em;
      transition: color 0.2s;
    }
    .social-link:hover { color: var(--accent); }
    .footer-copy { font-size: 0.8rem; color: var(--muted); }

    /* RESPONSIVE */
    @media (max-width: 768px) {
      .nav-links { display: none; flex-direction: column; position: absolute; top: 100%; left: 0; right: 0; background: var(--bg); padding: 16px 24px; gap: 16px; border-bottom: 1px solid var(--border); }
      .nav-links.show { display: flex; }
      .nav-toggle { display: block; }
      .hero { min-height: 70vh; }
      .hero-headline { font-size: 2.5rem; }
      .service-row { flex-direction: column; }
      .form-row { flex-direction: column; }
      .contact-details-row { flex-direction: column; gap: 12px; }
      .hours-list { columns: 1; }
      .gallery-grid { grid-template-columns: repeat(2, 1fr); }
    }

    @media (max-width: 480px) {
      body { font-size: 15px; }
      .hero-headline { font-size: 2rem; }
      .section-title { font-size: 1.5rem; }
      .nav-links { gap: 10px; flex-wrap: wrap; }
      .pull-quote { padding-left: 40px; }
      .gallery-grid { grid-template-columns: 1fr 1fr; gap: 8px; }
    }
  </style>
</head>
<body>
  <nav class="site-nav">
    <div class="nav-inner">
      <a href="#" class="nav-brand">${bSafe}</a>
      <button class="nav-toggle" aria-label="Toggle menu" onclick="this.parentElement.querySelector('.nav-links').classList.toggle('show')">
        <span></span><span></span><span></span>
      </button>
      <div class="nav-links">${navHtml}</div>
    </div>
  </nav>
  ${promotionHtml}
  ${heroHtml}
  ${aboutHtml}
  ${servicesHtml}
  ${galleryHtml}
  ${testimonialsHtml}
  ${faqHtml}
  ${priceHtml}
  ${hoursHtml}
  ${contactHtml}
  ${footerHtml}
</body>
</html>`;
  },
};
